import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { api } from '../services/api';
import { APP_CONFIG } from '../config/constants';

interface HeaderProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const Header: React.FC<HeaderProps> = ({ showToast }) => {
  const { user, signOut, loading } = useAuth();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);

  // Charger le profil utilisateur pour vérifier s'il est déjà tuteur-trice
  useEffect(() => {
    if (user) {
      const loadProfile = async () => {
        try {
          const { data } = await api.profiles.getByUserId(user.id);
          setUserProfile(data);
        } catch (error) {
        }
      };
      loadProfile();
    } else {
      setUserProfile(null);
    }
  }, [user]);

  const handleSignOut = async () => {
    try {
      const { error } = await signOut();
      if (error) {
        showToast('Erreur lors de la déconnexion', 'error');
      } else {
        showToast('Déconnexion réussie !', 'success');
        navigate('/');
      }
    } catch (error: any) {
      showToast('Erreur lors de la déconnexion', 'error');
    }
  };

  const handleSignup = () => {
    navigate('/signup');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <header className="bg-heg-blue shadow-sm sticky top-0 z-40">
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img 
              src="/logo-heg-white.svg" 
              alt="Logo HEG Genève" 
              className="h-8 w-auto object-contain"
            />
            <h1 className="text-sm md:text-base font-bold text-white font-heading">
              <span className="hidden sm:inline">{APP_CONFIG.name}</span>
            </h1>
          </Link>
          

          
          <div className="flex items-center space-x-3">
            {loading ? (
              <div className="px-4 py-2">
                <i className="fas fa-spinner fa-spin text-white"></i>
              </div>
            ) : user ? (
              // Utilisateur connecté
              <div className="flex items-center space-x-3">
                {/* Bouton Comment ça marche */}
                <Link 
                  to="/aide"
                  className="px-3 py-2 rounded-full bg-white/20 backdrop-blur-sm flex items-center gap-2 text-white hover:bg-white/30 transition-colors text-sm"
                  title="Comment ça marche ?"
                >
                  <i className="fas fa-question text-sm"></i>
                  <span className="hidden md:inline">Fonctionnement</span>
                </Link>
                <div className="hidden md:flex items-center space-x-2 text-white">
                  <i className="fas fa-user-circle"></i>
                  <span className="text-sm">
                    {user.user_metadata?.first_name || user.email?.split('@')[0]}
                  </span>
                </div>

                {/* Badge de notification */}
                <button 
                  onClick={() => navigate('/profile/requests')}
                  className="relative w-10 h-10 flex items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors"
                  aria-label="Demandes"
                >
                  <i className="fas fa-heart text-base"></i>
                  {unreadCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-yellow-400 text-red-800 text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center animate-pulse">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                  {/* libellé masqué pour homogénéiser les tailles */}
                </button>

                <button 
                  onClick={() => navigate('/profile')}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-heg-blue-light text-white hover:bg-heg-blue transition-colors"
                  aria-label="Mon profil"
                >
                  <i className="fas fa-user-cog text-base"></i>
                </button>
                <button 
                  onClick={handleSignOut}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors"
                  aria-label="Se déconnecter"
                >
                  <i className="fas fa-sign-out-alt text-base"></i>
                </button>
              </div>
                          ) : (
              // Utilisateur non connecté
              <>
                {/* Bouton Comment ça marche */}
                <Link 
                  to="/aide"
                  className="px-3 py-2 rounded-full bg-white/20 backdrop-blur-sm flex items-center gap-2 text-white hover:bg-white/30 transition-colors text-sm"
                  title="Comment ça marche ?"
                >
                  <i className="fas fa-question text-sm"></i>
                  <span className="hidden md:inline">Fonctionnement</span>
                </Link>
                {/* Afficher "Devenir tuteur-trice" seulement si l'utilisateur n'est pas connecté ou n'a pas de profil tuteur-trice */}
                {(!user || !userProfile || (userProfile.role !== 'repetiteur' && userProfile.role !== 'les_deux')) && (
                                   <button 
                   onClick={handleSignup}
                   className="px-4 py-2 rounded-full bg-green-600 text-white font-medium hover:bg-green-700 transition-colors text-sm md:text-base"
                 >
                   <span className="hidden md:inline">S'inscrire</span>
                   <i className="fas fa-user-plus md:ml-2"></i>
                 </button>
                )}
                {!user && (
                  <button 
                    onClick={handleLogin}
                    className="px-4 py-2 rounded-full bg-heg-blue-light text-white font-medium hover:bg-heg-blue transition-colors text-sm md:text-base"
                  >
                    <span className="hidden md:inline">Se connecter</span>
                    <i className="fas fa-sign-in-alt md:ml-2"></i>
                  </button>
                )}
              </>
            )}
            
            <button 
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <i className="fas fa-bars text-base"></i>
            </button>
          </div>
        </div>
      </div>
      
      {/* Menu mobile */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-heg-blue px-4 py-3">
          <div className="flex flex-col space-y-3">
            <Link 
              to="/tutors" 
              className="flex items-center text-white hover:text-gray-200"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="fas fa-search text-blue-400 mr-2"></i>
              Trouver un-e tuteur-trice
            </Link>
            <Link 
              to="/aide" 
              className="flex items-center text-white hover:text-gray-200"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="fas fa-question-circle text-green-400 mr-2"></i>
              Fonctionnement
            </Link>
            
            {/* Boutons d'authentification mobile */}
            <div className="border-t border-heg-blue-light pt-3 mt-3">
              {user ? (
                <div className="space-y-2">
                  <div className="text-white text-sm">
                    <i className="fas fa-user-circle mr-2"></i>
                    {user.user_metadata?.first_name || user.email?.split('@')[0]}
                  </div>
                  <button 
                    onClick={() => {
                      navigate('/profile');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left text-white/80 hover:text-white"
                  >
                    <i className="fas fa-user-cog mr-2"></i>
                    Mon profil
                  </button>
                  <button 
                    onClick={() => {
                      handleSignOut();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left text-red-300 hover:text-red-100"
                  >
                    <i className="fas fa-sign-out-alt mr-2"></i>
                    Se déconnecter
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <button 
                    onClick={() => {
                      handleSignup();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left text-white/80 hover:text-white"
                  >
                    <i className="fas fa-user-plus mr-2"></i>
                    S'inscrire
                  </button>
                  <button 
                    onClick={() => {
                      handleLogin();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left text-white/80 hover:text-white"
                  >
                    <i className="fas fa-sign-in-alt mr-2"></i>
                    Se connecter
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}; 